import { FormControl, OutlinedInput } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { Input, type InputProps } from './Input';

const StyledCreditsInputLarge = styled(OutlinedInput)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3), // Adjusted margin
  },
  '&.MuiInputBase-root.MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    fontSize: '1.5rem',
    border: '1px solid',
    borderColor: 'var(--input-border-color)',
    borderRadius: 4,
    color: 'var(--input-color)',
    boxSizing: 'none',
    margin: '0px',
    transition: theme.transitions.create(['border-color', 'background-color']),
    '&::placeholder': {
      color: 'var(--input-placeholder-color)',
      fontSize: '1rem', // Adjusted placeholder font size
      fontWeight: 400,
      opacity: 1,
    },
  },
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    height: '24px',
  },
  '&.MuiInputBase-root.MuiOutlinedInput-root.Mui-focused': {
    borderColor: 'var(--input-focus-border-color)',
    outline: 'none',
    boxSizing: 'none',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const DarkStylesLarge = {
  '--input-background-color': '#343337',
  '--input-border-color': 'rgba(255, 255, 255, 0.04)',
  '--input-color': 'rgba(255, 255, 255, 1)',
  '--input-focus-border-color': 'rgba(255, 255, 255, 0.4)',
  '--input-label-color': 'rgba(255, 255, 255, 0.8)',
  '--input-placeholder-color': 'rgba(255, 255, 255, 0.4)',
};

type CreditsInputProps = Omit<InputProps, 'size' | 'variant' | 'label'> & {
  label?: string;
  helperText?: string;
};

export const CreditsInput = ({
  label,
  helperText,
  id,
  startAdornment,
  error,
  required,
  ...props
}: CreditsInputProps) => {
  const isFpl = useFplMigrationFlag();
  const displayLabel = label != null && required ? `${label} *` : label;

  if (isFpl) {
    return (
      <Input
        {...props}
        id={id}
        size="large"
        label={displayLabel}
        helperText={helperText}
        error={error}
        required={required}
        startAdornment={startAdornment}
      />
    );
  }

  return (
    <FormControl variant="standard" sx={{ ...DarkStylesLarge, width: '100%' }}>
      <StyledCreditsInputLarge {...props} required={required} error={error} startAdornment={startAdornment} id={id} />
      {helperText ? (
        <TextSmRg color={error ? color.Weavy_Error : color.White80_T} sx={{ mt: 1, textAlign: 'left' }}>
          {helperText}
        </TextSmRg>
      ) : null}
    </FormControl>
  );
};
